import { FieldSize, Size, t, useNumericInput, type NumericKind } from '@bms2/shared';
import { FieldShell } from './FieldShell';
import styles from './field.module.css';

/**
 * Number, Currency and Percent input (piece 3): one component at three
 * precisions and suffixes, a replacement built on type=text with a decimal
 * keypad because type=number silently discards what it cannot parse. Values
 * are right-aligned in tabular figures, there are no spinner arrows, and
 * currency edits in dollars while storing integer cents.
 */
export interface NumericInputProps {
  label: string;
  kind: NumericKind;
  /** Stored units: integer cents for currency, the number itself otherwise. Null is empty. */
  value: number | null;
  onChange: (value: number | null) => void;
  decimals?: number;
  required?: boolean;
  error?: string | null;
  disabled?: boolean;
  name?: string;
  labelHidden?: boolean;
  /** What an empty box means, where blank is an answer rather than a gap: the credit limit's organization default. */
  hint?: string | null;
  /** As wide as this many characters, where that length is exactly known; it wins over `size` (decision 0297). */
  chars?: number;
  /** Which of the widths the box takes, Column in a line table's cell; a number, a rate or a count is Small (decisions 0583, 0647). */
  size?: FieldSize;
}

export const numericInputDocumentedStates = ['number', 'currency', 'percent', 'quantity'] as const;

export function NumericInput({ label, kind, value, onChange, decimals, required, error, disabled, name, labelHidden, hint, chars, size = Size.Medium }: NumericInputProps) {
  const numeric = useNumericInput(decimals === undefined ? { kind, value, onChange } : { kind, value, onChange, decimals });
  const shownError = error ?? (numeric.invalid ? t('numeric.invalid') : null);
  return (
    <FieldShell label={label} labelHidden={labelHidden ?? false} required={required ?? false} error={shownError} disabled={disabled ?? false} hint={hint ?? null} size={size} {...(chars ? { chars } : {})}>
      {({ inputId, messageId }) => (
        <>
          {numeric.prefix ? (
            <span className={styles.adornment} aria-hidden="true">
              {numeric.prefix}
            </span>
          ) : null}
          <input
            id={inputId}
            className={`${styles.input} ${styles.numeric} ${styles.withIcon}`}
            type="text"
            inputMode="decimal"
            name={name}
            value={numeric.text}
            required={required}
            disabled={disabled}
            data-kind={kind}
            aria-invalid={shownError ? true : undefined}
            aria-describedby={shownError ? messageId : undefined}
            onFocus={numeric.beginEdit}
            onChange={(e) => numeric.type(e.target.value)}
            onBlur={numeric.commit}
          />
          {numeric.suffix ? (
            <span className={styles.adornment} aria-hidden="true">
              {numeric.suffix}
            </span>
          ) : null}
        </>
      )}
    </FieldShell>
  );
}
